import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

// Shared "waiting for mail delivery" animation. Originally implemented inline in
// CreateBookingModal; extracted so the proactive compose feature reuses the exact
// same visual feedback. Pure CSS keyframes, no external animation dependency.

const ringSpinSlow = keyframes`
  to { transform: rotate(360deg); }
`;

const ringSpinReverse = keyframes`
  to { transform: rotate(-360deg); }
`;

const haloBreathe = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
`;

const iconFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const morphIn = keyframes`
  0% { transform: scale(0.55); opacity: 0; }
  60% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

export default function MailDeliveryAnimation({ status = 'pending', size = 200 }) {
  const isSuccess = status === 'delivered';
  const isProblem = status === 'failed' || status === 'bounced';
  const isSkipped = status === 'skipped';
  const isPending = !isSuccess && !isProblem && !isSkipped;

  const accentColor = isSuccess ? 'success.main' : isProblem ? 'error.main' : 'primary.main';

  return (
    <Box
      sx={{
        position: 'relative',
        height: size,
        width: size,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 18,
          borderRadius: '50%',
          background: theme =>
            `radial-gradient(closest-side, ${theme.palette.action.hover}, transparent 72%)`,
          animation: isPending ? `${haloBreathe} 3.6s ease-in-out infinite` : 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />

      {isPending && (
        <>
          <Box
            component='svg'
            viewBox='0 0 100 100'
            sx={{
              position: 'absolute',
              inset: 0,
              color: accentColor,
              animation: `${ringSpinSlow} 3.2s linear infinite`,
            }}
          >
            <circle
              cx='50'
              cy='50'
              r='49'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.6'
              strokeDasharray='40 268'
              strokeLinecap='round'
              opacity={0.55}
            />
          </Box>
          <Box
            component='svg'
            viewBox='0 0 100 100'
            sx={{
              position: 'absolute',
              inset: 14,
              color: accentColor,
              animation: `${ringSpinReverse} 5.8s linear infinite`,
            }}
          >
            <circle
              cx='50'
              cy='50'
              r='49'
              fill='none'
              stroke='currentColor'
              strokeWidth='1'
              pathLength='100'
              strokeDasharray='1.5 8.5'
              strokeLinecap='round'
              opacity={0.32}
            />
          </Box>
        </>
      )}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isPending && (
          <Box
            sx={{
              display: 'flex',
              color: 'primary.main',
              animation: `${iconFloat} 3.4s ease-in-out infinite`,
            }}
          >
            <MailOutlineIcon sx={{ fontSize: 64 }} />
          </Box>
        )}
        {isSkipped && (
          <Box sx={{ display: 'flex', color: 'text.secondary' }}>
            <MailOutlineIcon sx={{ fontSize: 68 }} />
          </Box>
        )}
        {isSuccess && (
          <Box
            sx={{
              display: 'flex',
              color: 'success.main',
              animation: `${morphIn} 480ms cubic-bezier(.2,.8,.2,1) both`,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 84 }} />
          </Box>
        )}
        {isProblem && (
          <Box
            sx={{
              display: 'flex',
              color: 'error.main',
              animation: `${morphIn} 480ms cubic-bezier(.2,.8,.2,1) both`,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 84 }} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
